import { TPaginationParams, TPaginatedResponse } from '../__shared/shared.interfaces';

// ===================== COMMON TYPES =====================

export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  BLOCKED = 'blocked',
}

export enum UserType {
  PARENTS = 'parents',
  VISITORS = 'visitors',
  GUESTS = 'guests',
  INTERNAL_USERS = 'internal_users',
}

export interface BaseUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  profilePicture?: string;
  status: UserStatus;
  createdDate: string;
  lastActive: string;
  notes?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
}

// ===================== PARENT USER TYPES =====================

export enum ParentRelation {
  FATHER = 'father',
  MOTHER = 'mother',
  GUARDIAN = 'guardian',
  STEP_FATHER = 'step_father',
  STEP_MOTHER = 'step_mother',
  GRANDPARENT = 'grandparent',
  OTHER = 'other',
}

export enum MeetingType {
  PARENT_TEACHER = 'parent_teacher',
  DISCIPLINARY = 'disciplinary',
  ACADEMIC = 'academic',
  GENERAL = 'general',
}

export enum MeetingStatus {
  SCHEDULED = 'scheduled',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export interface StudentReference {
  studentId: string;
  studentName: string;
  grade: string;
  section: string;
}

export interface Meeting {
  id: string;
  date: string;
  type: MeetingType;
  teacher: string;
  subject: string;
  notes: string;
  status: MeetingStatus;
}

export interface CommunicationPreferences {
  email: boolean;
  sms: boolean;
  phone: boolean;
  push: boolean;
}

export interface ParentUser extends BaseUser {
  relation: ParentRelation;
  occupation?: string;
  workPhone?: string;
  emergencyContact: boolean;
  children: StudentReference[];
  meetingHistory: Meeting[];
  communicationPreferences: CommunicationPreferences;
}

export type ParentUserListApiResponse = TPaginatedResponse<ParentUser>;

export interface ParentUserParams extends TPaginationParams {
  status?: UserStatus | 'all';
  relation?: ParentRelation | 'all';
  searchQuery?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  instituteId?: string;
}

export interface CreateParentUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  countryCode: string;
  userName: string;
  userPassword: string;
  relation: ParentRelation;
  occupation?: string;
  workPhone?: string;
  emergencyContact: boolean;
  children: StudentReference[];
  communicationPreferences: CommunicationPreferences;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  notes?: string;
  userCategory?: string;
}

export interface CreateMeetingRequest {
  date: string;
  type: MeetingType;
  teacher: string;
  subject: string;
  notes: string;
  status: MeetingStatus;
}

// ===================== VISITOR USER TYPES =====================

export enum VisitorStatus {
  SCHEDULED = 'scheduled',
  CHECKED_IN = 'checked_in',
  CHECKED_OUT = 'checked_out',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
}

export enum SecurityClearance {
  BASIC = 'basic',
  STANDARD = 'standard',
  HIGH = 'high',
}

export interface VehicleDetails {
  vehicleNumber: string;
  vehicleType: string;
  vehicleModel: string;
  parkingSpot?: string;
}

export interface VisitRecord {
  id: string;
  date: string;
  purpose: string;
  duration: number;
  host: string;
  status: VisitorStatus;
}

export interface VisitorUser extends BaseUser {
  visitorId?: string;
  visitDate: string;
  visitTime: string;
  visitDuration?: number;
  visitPurpose: string;
  hostPerson: string;
  hostDepartment: string;
  visitStatus: VisitorStatus;
  checkInTime?: string;
  checkOutTime?: string;
  idDocumentType: string;
  idDocumentNumber: string;
  visitorBadgeNumber?: string;
  securityClearance: SecurityClearance;
  vehicleDetails?: VehicleDetails;
  visitHistory: VisitRecord[];
}

export type VisitorUserListApiResponse = TPaginatedResponse<VisitorUser>;

export interface VisitorUserParams extends TPaginationParams {
  status?: UserStatus | 'all';
  visitStatus?: VisitorStatus | 'all';
  visitDate?: string;
  searchQuery?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  instituteId?: string;
}

export interface CreateVisitorUserRequest {
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  email: string;
  phone: string;
  userName: string;
  userPassword: string;
  visitDate: string;
  visitTime: string;
  visitDuration?: number;
  visitPurpose: string;
  hostPerson: string;
  hostDepartment: string;
  idDocumentType: string;
  idDocumentNumber: string;
  securityClearance: SecurityClearance;
  vehicleDetails?: VehicleDetails;
  visitStatus?: VisitorStatus;
  status?: UserStatus;
  address: {
    street1: string;
    street2?: string;
    city: string;
    state: string;
    pinCode: string;
    country: string;
    isCurrent?: boolean;
  };
  remarks?: string;
  userCategory?: string;
}

export interface CheckInRequest {
  checkInTime?: string;
  visitorBadgeNumber?: string;
}

export interface CheckOutRequest {
  checkOutTime?: string;
}

// ===================== GUEST USER TYPES =====================

export enum GuestType {
  VENDOR = 'vendor',
  CONTRACTOR = 'contractor',
  SPEAKER = 'speaker',
  VOLUNTEER = 'volunteer',
  OFFICIAL = 'official',
  OTHER = 'other',
}

export enum AccessLevel {
  LIMITED = 'limited',
  STANDARD = 'standard',
  FULL = 'full',
}

export enum ServiceStatus {
  PENDING = 'pending',
  SCHEDULED = 'scheduled',
  ACTIVE = 'active',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export interface Accommodation {
  required?: boolean;
  roomNumber?: string;
  roomType?: string;
  building?: string;
  checkIn?: string;
  checkOut?: string;
  checkInDate?: string;
  checkOutDate?: string;
  specialRequirements?: string;
  specialRequests?: string;
}

export interface ServiceRecord {
  id: string;
  serviceName: string;
  description: string;
  startDate: string;
  endDate: string;
  cost?: number;
  status: ServiceStatus;
}

export interface DocumentRecord {
  id: string;
  type: string;
  name: string;
  uploadDate: string;
  verified: boolean;
}

export interface GuestUser extends BaseUser {
  userId?: string;
  middleName?: string;
  fullName?: string;
  phoneNumber?: string;
  guestType: GuestType;
  organization?: string;
  designation?: string;
  eventId?: string;
  eventName?: string;
  invitedBy: string;
  invitationDate: string;
  accessLevel: AccessLevel;
  accommodation?: Accommodation;
  services: ServiceRecord[];
  documents: DocumentRecord[];
}

export type GuestUserListApiResponse = TPaginatedResponse<GuestUser>;

export interface GuestUserParams extends TPaginationParams {
  guestType?: GuestType | 'all';
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  eventId?: string;
  instituteId?: string;
}

export interface CreateGuestUserRequest {
  firstName: string;
  lastName: string;
  middleName?: string;
  dateOfBirth?: string;
  email: string;
  phone: string;
  userName: string;
  userPassword: string;
  guestType: GuestType;
  organization?: string;
  designation?: string;
  eventId?: string;
  eventName?: string;
  invitedBy: string;
  invitationDate: string;
  accessLevel: AccessLevel;
  status?: UserStatus;
  accommodation?: Accommodation;
  services?: Omit<ServiceRecord, 'id'>[];
  documents?: Omit<DocumentRecord, 'id'>[];
  address: {
    street1: string;
    street2?: string;
    city: string;
    state: string;
    pinCode: string;
    country: string;
    isCurrent?: boolean;
  };
  userCategory?: string;
}

export interface CreateServiceRequest {
  serviceName: string;
  description: string;
  startDate: string;
  endDate: string;
  cost?: number;
  status: ServiceStatus;
}

export interface UpdateServiceStatusRequest {
  status: ServiceStatus;
}

// ===================== SUMMARY STATISTICS =====================

export interface UsersSummary {
  totalUsers: number;
  activeUsers: number;
  pendingUsers: number;
  blockedUsers: number;
  recentlyAdded: number;
  statusDistribution: {
    active: number;
    inactive: number;
    pending: number;
    blocked: number;
  };
}

export interface VisitorsSummary {
  totalVisitors: number;
  checkedInVisitors: number;
  checkedOutVisitors: number;
  scheduledVisitors: number;
}

// ===================== INTERNAL USER TYPES =====================

export interface InternalUser extends BaseUser {
  userRole: string;
  hasStaffRecord: boolean;
  middleName?: string;
}

export interface InternalUserParams extends TPaginationParams {
  instituteId: string;
  search?: string;
}

export interface CreateInternalUserRequest {
  firstName: string;
  middleName?: string;
  lastName: string;
  gender: string;
  dateOfBirth?: string;
  status?: string;
  userName: string;
  userPassword: string;
  userRole: string;
  userCategory?: string;
  instituteId: string;
  createdBy: string;
  address: {
    street1: string;
    city: string;
    state: string;
    pinCode: string;
    country: string;
  };
  contact: {
    countryCode: string;
    phoneNumber: string;
    email: string;
  };
}

export type TInternalUserListApiResponse = TPaginatedResponse<InternalUser>;

// ===================== USERS BY CATEGORY =====================

export type UsersByCategoryParams = TPaginationParams & {
  category: string;
  search?: string;
};

/**
 * Raw user shape returned by `GET /v1/users/by-category` — mirrors the
 * `UserByCategoryDto` schema in docs/apis-documentation/openapi.json.
 * Shared across all categories (visitor | guardian | staff | external | student).
 */
export interface UserByCategoryDto {
  _id: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  gender: 'male' | 'female' | 'trans';
  date_of_birth?: string;
  description?: string;
  user_name?: string;
  user_role: string;
  user_status?: string;
  user_category: string;
  institute_id?: string;
  profile_picture_ref?: string;
  last_active?: string;
  created_by?: string;
  updated_by?: string;
  created_at?: string;
  updated_at?: string;
  email?: string;
  phone?: string;
}

/** FE-facing (camelCase) item mapped from {@link UserByCategoryDto}. */
export interface UserByCategoryItem {
  id: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  gender?: string;
  dateOfBirth?: string;
  description?: string;
  userName?: string;
  email?: string;
  phone?: string;
  userRole: string;
  userStatus: string;
  userCategory: string;
  instituteId?: string;
  profilePicture?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  lastActive?: string;
}

export type TUsersByCategoryResponse = TPaginatedResponse<UserByCategoryItem>;

